import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Avatar, AvatarFallback } from './avatar';

interface MentionListProps {
  items: Array<{ id: string; label: string; email?: string }>;
  command: (item: { id: string; label: string }) => void;
}

export const MentionList = forwardRef<
  { onKeyDown: (props: any) => boolean },
  MentionListProps
>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) {
      props.command(item);
    }
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => setSelectedIndex(0), [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg py-2 px-1 max-h-60 overflow-y-auto">
      {props.items.length ? (
        props.items.map((item, index) => (
          <button
            key={item.id}
            className={`flex items-center w-full text-left px-3 py-2 rounded-md text-sm ${
              index === selectedIndex
                ? 'bg-blue-500 text-white'
                : 'hover:bg-gray-100'
            }`}
            onClick={() => selectItem(index)}
          >
            <Avatar className="h-6 w-6 mr-2">
              <AvatarFallback className="text-xs">
                {getInitials(item.label)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{item.label}</div>
              {item.email && (
                <div className="text-xs opacity-75 truncate">{item.email}</div>
              )}
            </div>
          </button>
        ))
      ) : (
        <div className="px-3 py-2 text-sm text-gray-500">No users found</div>
      )}
    </div>
  );
});

MentionList.displayName = 'MentionList';
